import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { dealActiveVersionState } from '@/states/deal-active-version.state';
import { useAtomValue } from 'jotai';
import { Check, CheckCheck } from 'lucide-react';

interface ValidationTicksProps {
  direction?: 'left' | 'right';
  iconOnly?: boolean;
}

const LABEL_VISIBLE_MS = 2000;

function ValidationTicks({
  direction = 'right',
  iconOnly = false,
}: ValidationTicksProps) {
  const { t, i18n } = useTranslation();
  const dealActiveVersion = useAtomValue(dealActiveVersionState);

  const validationContentKey = `${dealActiveVersion?.isValidatedByBrand}-${dealActiveVersion?.isValidatedByChoose}`;

  const [compact, setCompact] = useState(false);
  const slidingCompact = compact && iconOnly;

  useEffect(() => {
    if (validationContentKey === null) return;
    setCompact(false);
    const id = window.setTimeout(() => setCompact(true), LABEL_VISIBLE_MS);
    return () => window.clearTimeout(id);
  }, [validationContentKey, i18n.language]);

  if (!dealActiveVersion) return null;

  const { isValidatedByBrand, isValidatedByChoose } = dealActiveVersion;

  if (!isValidatedByBrand && !isValidatedByChoose) return null;

  const bothValidated = isValidatedByBrand && isValidatedByChoose;
  const Icon = bothValidated ? CheckCheck : Check;

  let label: string;
  if (bothValidated) {
    label = t('components.offer_header.validation.both');
  } else if (isValidatedByBrand) {
    label = t('components.offer_header.validation.brand');
  } else {
    label = t('components.offer_header.validation.choose');
  }

  const slidingText = (
    <span
      className={cn(
        'grid overflow-hidden transition-[grid-template-columns] duration-300 ease-in-out motion-reduce:transition-none',
        slidingCompact
          ? 'grid-cols-[0fr] group-hover:grid-cols-[1fr]'
          : 'grid-cols-[1fr]'
      )}
    >
      <span className="min-w-0 overflow-hidden whitespace-nowrap text-xs font-medium">
        {direction === 'left' ? <>{label}&nbsp;</> : <>&nbsp;{label}</>}
      </span>
    </span>
  );

  return (
    <span className="group inline-flex h-9 cursor-default items-center rounded-sm bg-green-50 px-2 text-green-600">
      {direction === 'left' && slidingText}
      <Icon className="size-5 shrink-0 px-0.5" />
      {direction === 'right' && slidingText}
    </span>
  );
}

export { ValidationTicks };
